import React, { useState, useEffect } from 'react';
import BookCard from './BookCard';
import { FilterIcon, XIcon } from '@heroicons/react/outline';
import PageTitle from '../common/PageTitle';
import axiosInstance from '../../config/axios';
import { toastService } from '../../services/toast';

const Store = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    priceRange: 'all',
    sortBy: 'newest'
  });

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await axiosInstance.get('/api/store/books');
        setBooks(response.data.map(book => ({
          ...book,
          coverImage: book.cover_url,
          oldPrice: book.old_price,
          category: book.categories?.[0]?.id?.toString() || 'all'
        })));
      } catch (error) {
        console.error('Error fetching books:', error);
        toastService.error('حدث خطأ أثناء جلب الكتب');
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const categories = [
    { id: 'all', name: 'جميع الكتب' },
    { id: '1', name: 'البرمجة' },
    { id: '2', name: 'التصميم' },
    { id: '3', name: 'إدارة الأعمال' }
  ];

  const priceRanges = [
    { id: 'all', name: 'جميع الأسعار' },
    { id: 'under-50', name: 'أقل من 50 ر.س' },
    { id: '50-100', name: '50 - 100 ر.س' },
    { id: 'over-100', name: 'أكثر من 100 ر.س' }
  ];

  const sortOptions = [
    { id: 'newest', name: 'الأحدث' },
    { id: 'price-low', name: 'السعر: من الأقل للأعلى' },
    { id: 'price-high', name: 'السعر: من الأعلى للأقل' },
    { id: 'popular', name: 'الأكثر مبيعاً' }
  ];

  const filteredBooks = books.filter(book => {
    if (filters.category !== 'all' && book.category !== filters.category) {
      return false;
    }

    if (filters.priceRange !== 'all') {
      const price = parseFloat(book.price);
      switch (filters.priceRange) {
        case 'under-50':
          if (price >= 50) return false;
          break;
        case '50-100':
          if (price < 50 || price > 100) return false;
          break;
        case 'over-100':
          if (price <= 100) return false;
          break;
      }
    }

    return true;
  });

  const sortedBooks = [...filteredBooks].sort((a, b) => {
    switch (filters.sortBy) {
      case 'price-low':
        return parseFloat(a.price) - parseFloat(b.price);
      case 'price-high':
        return parseFloat(b.price) - parseFloat(a.price);
      case 'popular':
        return (b.downloads_count || 0) - (a.downloads_count || 0);
      case 'newest':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <PageTitle title="متجر الكتب" />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-blue-800 via-blue-700 to-blue-600 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
        {/* زخارف الخلفية */}
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute -top-12 -left-12 w-48 h-48 md:-top-24 md:-left-24 md:w-96 md:h-96 rounded-full bg-white dark:bg-blue-500"></div>
          <div className="absolute top-1/4 left-1/2 w-32 h-32 md:w-64 md:h-64 rounded-full bg-white dark:bg-blue-500"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 md:w-80 md:h-80 rounded-full bg-white dark:bg-blue-500"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-24 pb-12 md:pt-32 md:pb-16">
          <div className="text-center relative z-20">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              مكتبة دوز للكتب الرقمية
            </h1>
            <div className="w-24 h-1 bg-yellow-400 dark:bg-yellow-500 rounded-full mb-6 mx-auto"></div>
            <p className="text-lg text-white text-opacity-90 dark:text-gray-200 mb-8 max-w-2xl mx-auto">
              اكتشف مجموعة متنوعة من الكتب الرقمية في مختلف المجالات العلمية والأدبية
            </p>
            
            <div className="flex justify-center">
              <div className="bg-white bg-opacity-20 dark:bg-gray-700 rounded-full px-4 py-1.5 text-sm text-white flex items-center backdrop-blur-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                دعم فني مستمر
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="min-h-screen relative">

          {/* Main Content */}
          <div>
            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    جميع الكتب
                    <div className="mt-1 flex items-center">
                      <div className="h-1 w-16 bg-primary-500 rounded-full"></div>
                      <div className="h-1 w-4 bg-primary-500/60 rounded-full mr-1"></div>
                      <div className="h-1 w-2 bg-primary-500/40 rounded-full mr-1"></div>
                    </div>
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">اختر من مجموعة متنوعة من الكتب الرقمية</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {books.length} كتاب
                  </span>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                  >
                    <FilterIcon className="h-4 w-4 ml-2" />
                    تصفية وترتيب
                  </button>
                </div>
              </div>

              {/* Active Filters */}
              {(filters.category !== 'all' || filters.priceRange !== 'all') && (
                <div className="mt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">الفلاتر النشطة:</span>
                    {filters.category !== 'all' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-800">
                        {categories.find(c => c.id === filters.category)?.name}
                        <button
                          onClick={() => setFilters({ ...filters, category: 'all' })}
                          className="mr-1 p-0.5 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-full transition-colors duration-200"
                        >
                          <XIcon className="h-4 w-4" />
                        </button>
                      </span>
                    )}
                    {filters.priceRange !== 'all' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-800">
                        {priceRanges.find(r => r.id === filters.priceRange)?.name}
                        <button
                          onClick={() => setFilters({ ...filters, priceRange: 'all' })}
                          className="mr-1 p-0.5 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-full transition-colors duration-200"
                        >
                          <XIcon className="h-4 w-4" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mb-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        التصنيف
                      </label>
                      <select
                        value={filters.category}
                        onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                      >
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        نطاق السعر
                      </label>
                      <select
                        value={filters.priceRange}
                        onChange={(e) => setFilters({ ...filters, priceRange: e.target.value })}
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                      >
                        {priceRanges.map((range) => (
                          <option key={range.id} value={range.id}>
                            {range.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        الترتيب حسب
                      </label>
                      <select
                        value={filters.sortBy}
                        onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                      >
                        {sortOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Books Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-[340px] bg-gray-200 dark:bg-gray-700 rounded-lg shadow-md" />
                    <div className="mt-4 space-y-3">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
                {sortedBooks.map((book) => (
                  <BookCard key={book.id} book={book} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Store;
